import { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { ArrowLeft, Heart, Loader2 } from "lucide-react";

/**
 * Support widget backed by the shared podcast-support Cloudflare Worker.
 * apiBase = "https://<worker-host>/v1/<show>"
 * theme   = optional colors (defaults match WVEdChat's navy/gold theme):
 *   { primary, onPrimary, accent, accentStrong, accentSoft, onAccent,
 *     text, textMuted, textFaint, border, track }
 */
const DEFAULT_THEME = {
  primary: "#f5b61c",
  onPrimary: "#0a1630",
  accent: "#f5b61c",
  accentStrong: "#dda00d",
  accentSoft: "rgba(245, 182, 28, 0.22)",
  onAccent: "#0a1630",
  link: "#8a5a00",
  text: "#0b1f4d",
  textMuted: "#4d5a78",
  textFaint: "#66718d",
  border: "rgba(11, 31, 77, 0.18)",
  track: "rgba(11, 31, 77, 0.07)",
};
const LABELS = { once: "One-time", month: "Monthly", year: "Yearly" };
const SUFFIX = { once: "", month: "/mo", year: "/yr" };
const stripeCache = {};

export default function SupportWidget({ apiBase, theme }) {
  const t = { ...DEFAULT_THEME, ...theme };
  const vars = {
    "--sw-primary": t.primary,
    "--sw-on-primary": t.onPrimary,
    "--sw-accent": t.accent,
    "--sw-accent-strong": t.accentStrong,
    "--sw-accent-soft": t.accentSoft,
    "--sw-on-accent": t.onAccent,
    "--sw-link": t.link,
    "--sw-text": t.text,
    "--sw-text-muted": t.textMuted,
    "--sw-text-faint": t.textFaint,
    "--sw-border": t.border,
    "--sw-track": t.track,
  };
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState("");
  const [interval, setFreq] = useState("month");
  const [amount, setAmount] = useState(null); // cents
  const [custom, setCustom] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // finished checkout status

  // Load config, and check for a returning checkout (?session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");

    fetch(`${apiBase}/config`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((c) => {
        setCfg(c);
        setAmount(c.presets?.month?.[1] ?? c.presets?.month?.[0] ?? null);
      })
      .catch(() => setError("Support is temporarily unavailable. Please try again later."));

    if (sessionId) {
      fetch(`${apiBase}/session?session_id=${encodeURIComponent(sessionId)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((s) => s && setResult(s))
        .finally(() => window.history.replaceState(null, "", window.location.pathname));
    }
  }, [apiBase]);

  const stripePromise = useMemo(() => {
    if (!cfg?.publishableKey) return null;
    return (stripeCache[cfg.publishableKey] ??= loadStripe(cfg.publishableKey));
  }, [cfg]);

  const fmt = (cents) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (cfg?.currency || "usd").toUpperCase(),
      minimumFractionDigits: cents % 100 ? 2 : 0,
    }).format(cents / 100);

  const pickInterval = (i) => {
    setFreq(i);
    setCustom("");
    setAmount(cfg.presets?.[i]?.[1] ?? cfg.presets?.[i]?.[0] ?? null);
  };

  const onCustom = (v) => {
    const clean = v.replace(/[^\d.]/g, "");
    setCustom(clean);
    const cents = Math.round(parseFloat(clean) * 100);
    setAmount(Number.isFinite(cents) ? cents : null);
  };

  const valid = cfg && amount != null && amount >= cfg.minAmount && amount <= cfg.maxAmount;

  const start = async () => {
    if (!valid) return;
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${apiBase}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount, interval }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Could not start checkout.");
      setClientSecret(data.clientSecret);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // --- Thank-you state -----------------------------------------------------
  if (result?.status === "complete") {
    return (
      <div data-testid="support-thanks" className="text-center py-8" style={vars}>
        <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-[var(--sw-accent-soft)] text-[var(--sw-link)]">
          <Heart size={26} />
        </div>
        <h3 className="mt-4 font-display text-2xl font-semibold text-[var(--sw-text)]">Thank you!</h3>
        <p className="mt-2 text-[var(--sw-text-muted)] max-w-md mx-auto">
          Your {result.interval === "once" ? "gift" : `${LABELS[result.interval].toLowerCase()} support`} of{" "}
          {result.amount != null && fmt(result.amount)} means a lot.
          {result.email && <> A receipt is on its way to {result.email}.</>}
        </p>
        <button
          onClick={() => { setResult(null); setClientSecret(""); }}
          className="mt-6 text-sm font-semibold text-[var(--sw-link)] underline underline-offset-4"
        >
          Make another gift
        </button>
      </div>
    );
  }

  if (error && !cfg) return <p className="text-center text-[var(--sw-text-muted)] py-8" style={vars}>{error}</p>;
  if (!cfg) {
    return (
      <div className="flex justify-center py-10 text-[var(--sw-text-faint)]" style={vars}>
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  // --- Embedded Stripe checkout -------------------------------------------
  if (clientSecret && stripePromise) {
    return (
      <div data-testid="support-checkout" style={vars}>
        <button
          onClick={() => setClientSecret("")}
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--sw-link)] hover:text-[var(--sw-text)]"
        >
          <ArrowLeft size={16} /> Change amount ({fmt(amount)}{SUFFIX[interval]})
        </button>
        <EmbeddedCheckoutProvider
          key={clientSecret}
          stripe={stripePromise}
          options={{ clientSecret }}
        >
          <div className="rounded-lg overflow-hidden bg-white">
            <EmbeddedCheckout />
          </div>
        </EmbeddedCheckoutProvider>
      </div>
    );
  }

  // --- Amount picker -------------------------------------------------------
  const presets = cfg.presets?.[interval] || [];
  // Fit any preset count from wrangler.toml into even rows (e.g. 6 -> 3x2, 4 -> 4x1)
  const n = presets.length;
  const cols = n % 4 === 0 ? 4 : n % 3 === 0 ? 3 : Math.min(n, 4);
  const gridCls = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-2 sm:grid-cols-4" }[cols];
  return (
    <div data-testid="support-widget" className="max-w-md mx-auto" style={vars}>
      <div role="tablist" className="grid grid-cols-3 rounded-full bg-[var(--sw-track)] p-1">
        {cfg.intervals.map((i) => (
          <button
            key={i}
            role="tab"
            aria-selected={interval === i}
            data-testid={`support-interval-${i}`}
            onClick={() => pickInterval(i)}
            className={`rounded-full py-2 text-sm font-semibold transition-colors ${
              interval === i ? "bg-[var(--sw-primary)] text-[var(--sw-on-primary)] shadow" : "text-[var(--sw-text-muted)] hover:text-[var(--sw-text)]"
            }`}
          >
            {LABELS[i]}
          </button>
        ))}
      </div>

      <div className={`mt-5 grid ${gridCls} gap-2`}>
        {presets.map((p) => (
          <button
            key={p}
            data-testid={`support-preset-${p}`}
            onClick={() => { setAmount(p); setCustom(""); }}
            className={`rounded-lg border py-3 font-semibold transition-colors ${
              amount === p && !custom
                ? "border-[var(--sw-accent)] bg-[var(--sw-accent-soft)] text-[var(--sw-link)]"
                : "border-[var(--sw-border)] text-[var(--sw-text-muted)] hover:border-[var(--sw-accent)]"
            }`}
          >
            {fmt(p)}
            <span className="text-xs font-normal text-[var(--sw-text-faint)]">{SUFFIX[interval]}</span>
          </button>
        ))}
      </div>

      <label className="mt-3 flex items-center rounded-lg border border-[var(--sw-border)] focus-within:border-[var(--sw-accent)] px-3">
        <span className="text-[var(--sw-text-faint)]">$</span>
        <input
          inputMode="decimal"
          placeholder="Other amount"
          value={custom}
          onChange={(e) => onCustom(e.target.value)}
          data-testid="support-custom-amount"
          className="w-full bg-transparent px-2 py-3 outline-none text-[var(--sw-text)]"
        />
        {interval !== "once" && <span className="text-sm text-[var(--sw-text-faint)]">{SUFFIX[interval]}</span>}
      </label>
      {amount != null && !valid && (
        <p className="mt-2 text-sm text-red-600">
          Please enter between {fmt(cfg.minAmount)} and {fmt(cfg.maxAmount)}.
        </p>
      )}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <button
        onClick={start}
        disabled={!valid || busy}
        data-testid="support-continue"
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--sw-accent)] hover:bg-[var(--sw-accent-strong)] disabled:opacity-50 disabled:hover:bg-[var(--sw-accent)] text-[var(--sw-on-accent)] font-bold py-3.5 transition-colors"
      >
        {busy ? <Loader2 size={18} className="animate-spin" /> : <Heart size={18} />}
        {valid ? `Give ${fmt(amount)}${SUFFIX[interval]}` : "Choose an amount"}
      </button>

      <p className="mt-4 text-center text-xs text-[var(--sw-text-faint)]">
        Secure payment by Stripe.
        {cfg.portalUrl && (
          <>
            {" "}Already a supporter?{" "}
            <a href={cfg.portalUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-[var(--sw-text)]">
              Manage your gift
            </a>
          </>
        )}
      </p>
    </div>
  );
}
